// Implements: F-VER-006, F-UIX-009
/**
 * Promoting a version, as one action that never rejects.
 *
 * It answers `null` when the pointer moved and the refusal itself when it did
 * not, so the container has one decision left: which notice to show. Nothing
 * is optimistic: the mockup is refetched and the page shows what the server
 * now holds.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { invalidateMockupPointer } from "../../../shared/api/invalidateMockupPointer";
import { promoteVersion } from "../api/versionsApi";

export function usePromoteVersion(): (
  mockupId: string,
  versionId: string,
) => Promise<ApiError | null> {
  const queryClient = useQueryClient();

  return useCallback(
    async (mockupId: string, versionId: string): Promise<ApiError | null> => {
      try {
        await promoteVersion(mockupId, versionId);
      } catch (cause) {
        return asApiError(cause);
      }
      // The gallery cards, the tree and the thread say which version is current too.
      await invalidateMockupPointer(queryClient, mockupId);
      return null;
    },
    [queryClient],
  );
}
